import { PDFArray, PDFBool, PDFDict, PDFDocument, PDFName, PDFNumber, PDFRawStream, PDFRef } from "pdf-lib";
import { chainKind, decodeStoredBytes, filterChain, readPalette, unpackSamples } from "./pdfStreamImage";
import { allocate, rgbaToGray, ssimDistortion, type Candidate } from "./rdAllocate";

/**
 * Shrinks the pictures INSIDE a PDF and leaves everything else alone -- text stays text (selectable,
 * searchable), pages keep their size, and links, bookmarks and form fields survive because the same
 * document is saved. This is what "compress" means for almost every real PDF: the bytes are in the
 * pictures, not the words.
 *
 * Pictures are found whatever their chain of filters (JPEG inside ASCII85, Flate with a PNG predictor,
 * LZW, run-length ...), and plain RGB, grey and palette pictures are handled. Anything unusual --
 * stencil masks, CMYK, JPEG 2000, JBIG2/CCITT scans -- is left exactly as is, and counted so the
 * tool can say so.
 */

export interface PdfImageInfo {
  ref: PDFRef;
  width: number;
  height: number;
  components: 1 | 3;
  kind: "jpeg" | "raw";
  /** Bits per sample as stored (raw pictures may be 1, 2, 4 or 8). */
  bpc: number;
  /** For palette pictures: the colour of every index as RGB triplets. */
  palette?: Uint8Array;
  /** Size of the picture's stored bytes in the file. */
  storedBytes: number;
}

/** What to do to every picture: no side longer than maxSide pixels (never enlarged), JPEG quality 0-1. */
export interface RecompressSetting {
  maxSide: number;
  quality: number;
}

/** Re-encodes one picture. Returns null to leave it alone. `stored` is the stream's bytes as stored (JPEG)
 * or already decoded to raw samples (raw). In a browser this draws to a canvas; tests pass a fake. */
export interface ImageCodec {
  reencode(info: PdfImageInfo, stored: Uint8Array, setting: RecompressSetting): Promise<{ bytes: Uint8Array; width: number; height: number } | null>;
  /** Optional: for each setting, the size the picture would have and how much worse it would look (0 = identical, see
   * rdAllocate.ssimDistortion). Lets the compressor spend its size budget picture by picture instead of by one rule for all. */
  profile?(info: PdfImageInfo, stored: Uint8Array, settings: RecompressSetting[]): Promise<({ bytes: number; distortion: number } | null)[]>;
}

/** Both codecs compare a picture with its original at this size (long side), so blur and blockiness show up. */
export const PROFILE_COMPARE_SIDE = 640;

const LADDER_ANCHORS: RecompressSetting[] = [
  { maxSide: 3000, quality: 0.9 },
  { maxSide: 2400, quality: 0.85 },
  { maxSide: 2000, quality: 0.8 },
  { maxSide: 1600, quality: 0.75 },
  { maxSide: 1400, quality: 0.7 },
  { maxSide: 1200, quality: 0.65 },
  { maxSide: 1000, quality: 0.6 },
  { maxSide: 800, quality: 0.55 },
  { maxSide: 650, quality: 0.5 },
  { maxSide: 500, quality: 0.4 },
  { maxSide: 400, quality: 0.3 },
  { maxSide: 300, quality: 0.25 },
];

/**
 * Sharpest to coarsest, with three steps between each pair of anchors above, so a target can be met
 * within a few percent instead of landing far under it. Sizes only ever go down along this list, so a
 * search can bisect it (about six passes for 45 steps).
 */
export const SETTING_LADDER: RecompressSetting[] = (() => {
  const out: RecompressSetting[] = [];
  const SUB = 4;
  for (let i = 0; i < LADDER_ANCHORS.length - 1; i++) {
    const a = LADDER_ANCHORS[i];
    const b = LADDER_ANCHORS[i + 1];
    for (let k = 0; k < SUB; k++) {
      const t = k / SUB;
      out.push({ maxSide: Math.round(a.maxSide * Math.pow(b.maxSide / a.maxSide, t)), quality: Math.round((a.quality + (b.quality - a.quality) * t) * 1000) / 1000 });
    }
  }
  out.push(LADDER_ANCHORS[LADDER_ANCHORS.length - 1]);
  return out;
})();

/** Balanced and Smallest: the longest side of a picture is limited to 1600 and 1100 pixels (about 145 and 100 dots per inch on a Letter page's long side). */
export const BALANCED_SETTING: RecompressSetting = { maxSide: 1600, quality: 0.75 };
/** Balanced mode: about 140 dpi on the long side of an 11 in page. */
export const BALANCED_140: RecompressSetting = { maxSide: 1540, quality: 0.75 };
export const SMALLEST_SETTING: RecompressSetting = { maxSide: 1100, quality: 0.6 };

/** Pictures smaller than this are not worth the loss of quality. */
const MIN_STORED_BYTES = 20 * 1024;
/** A replacement must be at least this much smaller than what was there. */
const MIN_GAIN = 0.95;

const num = (dict: PDFDict, key: string): number | undefined => {
  const v = dict.lookup(PDFName.of(key));
  return v instanceof PDFNumber ? v.asNumber() : undefined;
};

/** Channels of a plain colour space (grey 1, RGB 3), or null for anything else (CMYK, Lab, separations...). */
function plainComponents(cs: unknown): 1 | 3 | null {
  if (cs instanceof PDFName) {
    const n = cs.decodeText();
    return n === "DeviceRGB" || n === "RGB" || n === "CalRGB" ? 3 : n === "DeviceGray" || n === "G" || n === "CalGray" ? 1 : null;
  }
  if (cs instanceof PDFArray && cs.size() >= 1) {
    const head = cs.lookup(0);
    if (!(head instanceof PDFName)) return null;
    const h = head.decodeText();
    if (h === "ICCBased" && cs.size() === 2) {
      const profile = cs.lookup(1);
      if (profile instanceof PDFRawStream) {
        const n = num(profile.dict, "N");
        return n === 3 ? 3 : n === 1 ? 1 : null;
      }
      return null;
    }
    if (h === "CalRGB") return 3;
    if (h === "CalGray") return 1;
    if (h === "DeviceRGB" || h === "DeviceGray") return plainComponents(head);
  }
  return null;
}

/** Every picture in the document that can be shrunk safely, and how many stored bytes the others take. */
export function findImages(doc: PDFDocument): { images: PdfImageInfo[]; totalStoredBytes: number; skippedBytes: number; skippedCount: number } {
  const images: PdfImageInfo[] = [];
  let totalStoredBytes = 0;
  let skippedBytes = 0;
  let skippedCount = 0;
  for (const [ref, obj] of doc.context.enumerateIndirectObjects()) {
    if (!(obj instanceof PDFRawStream)) continue;
    const dict = obj.dict;
    if (dict.lookup(PDFName.of("Subtype"))?.toString() !== "/Image") continue;
    const stored = obj.getContentsSize();
    totalStoredBytes += stored;
    const info = describeImage(doc, ref, obj);
    if (info && stored >= MIN_STORED_BYTES) images.push(info);
    else if (!info && stored >= MIN_STORED_BYTES) {
      skippedBytes += stored;
      skippedCount++;
    }
  }
  return { images, totalStoredBytes, skippedBytes, skippedCount };
}

function describeImage(doc: PDFDocument, ref: PDFRef, obj: PDFRawStream): PdfImageInfo | null {
  const dict = obj.dict;
  const chain = filterChain(dict);
  const kind = chainKind(chain);
  if (!kind) return null;
  const mask = dict.lookup(PDFName.of("ImageMask"));
  if (mask instanceof PDFBool && mask.asBoolean()) return null;
  if (dict.has(PDFName.of("Decode")) || dict.has(PDFName.of("Mask"))) return null;
  const width = num(dict, "Width");
  const height = num(dict, "Height");
  if (!width || !height) return null;
  const cs = dict.lookup(PDFName.of("ColorSpace"));
  const bpc = num(dict, "BitsPerComponent") ?? 8;
  let components: 1 | 3 | null = plainComponents(cs);
  let palette: Uint8Array | undefined;
  if (!components) {
    const pal = readPalette(cs, plainComponents);
    if (pal && kind === "raw" && [1, 2, 4, 8].includes(bpc)) {
      palette = pal.palette;
      components = 3;
    }
  }
  if (!components) return null;
  if (kind === "jpeg" && bpc !== 8) return null;
  if (kind === "raw" && !([1, 2, 4, 8] as number[]).includes(bpc)) return null;
  // Grey with fewer than eight bits is spread to eight; colour needs eight.
  if (kind === "raw" && bpc !== 8 && !palette && components !== 1) return null;
  void doc;
  return { ref, width, height, components, kind, storedBytes: obj.getContentsSize(), bpc, palette };
}

/** The picture's samples as the codec expects them: a JPEG file, or width x height x components bytes. */
export function readImageBytes(stream: PDFRawStream, info: PdfImageInfo): Uint8Array | null {
  try {
    const chain = filterChain(stream.dict);
    const bytes = decodeStoredBytes(stream, chain);
    if (info.kind === "jpeg") return bytes;
    const perPixel = info.palette ? 1 : info.components;
    const samples = unpackSamples(bytes, info.width, info.height, perPixel, info.bpc);
    if (samples.length < info.width * info.height * perPixel) return null;
    const n = info.width * info.height;
    if (info.palette) {
      const out = new Uint8Array(n * 3);
      for (let i = 0; i < n; i++) {
        const k = Math.min(samples[i], info.palette.length / 3 - 1) * 3;
        out[i * 3] = info.palette[k];
        out[i * 3 + 1] = info.palette[k + 1];
        out[i * 3 + 2] = info.palette[k + 2];
      }
      return out;
    }
    if (info.bpc !== 8) {
      const scale = 255 / ((1 << info.bpc) - 1);
      const out = new Uint8Array(n);
      for (let i = 0; i < n; i++) out[i] = Math.round(samples[i] * scale);
      return out;
    }
    return samples.length === n * info.components ? samples : samples.subarray(0, n * info.components);
  } catch {
    return null;
  }
}

export interface RecompressResult {
  bytes: Uint8Array;
  /** How many pictures were replaced by smaller ones. */
  replaced: number;
  /** How many pictures were looked at. */
  considered: number;
}

export class CompressStopped extends Error {
  constructor() {
    super("Compression stopped");
    this.name = "CompressStopped";
  }
}

/** Opens the original bytes afresh, replaces pictures per `setting`, saves. */
export async function recompressImages(
  original: Uint8Array,
  setting: RecompressSetting | ((info: PdfImageInfo, index: number) => RecompressSetting | null),
  codec: ImageCodec,
  hooks: { onProgress?: (done: number, total: number) => void; shouldStop?: () => boolean } = {},
): Promise<RecompressResult> {
  const doc = await PDFDocument.load(original, { updateMetadata: false });
  const { images } = findImages(doc);
  let replaced = 0;
  for (let i = 0; i < images.length; i++) {
    if (hooks.shouldStop?.()) throw new CompressStopped();
    hooks.onProgress?.(i, images.length);
    const info = images[i];
    const stream = doc.context.lookup(info.ref);
    if (!(stream instanceof PDFRawStream)) continue;
    const stored = readImageBytes(stream, info);
    if (!stored) continue;
    const chosen = typeof setting === "function" ? setting(info, i) : setting;
    if (!chosen) continue; // the plan keeps this picture as it is
    let out: { bytes: Uint8Array; width: number; height: number } | null = null;
    try {
      out = await codec.reencode(info, stored, chosen);
    } catch {
      out = null; // a picture the browser can't decode stays as it was
    }
    if (!out || out.bytes.length >= info.storedBytes * MIN_GAIN) continue;
    const dict = doc.context.obj({
      Type: "XObject",
      Subtype: "Image",
      Width: out.width,
      Height: out.height,
      ColorSpace: "DeviceRGB", // a canvas always writes a three-channel JPEG
      BitsPerComponent: 8,
      Filter: "DCTDecode",
    }) as PDFDict;
    const smask = stream.dict.get(PDFName.of("SMask"));
    if (smask) dict.set(PDFName.of("SMask"), smask); // transparency is its own picture and keeps its own size
    const interpolate = stream.dict.get(PDFName.of("Interpolate"));
    if (interpolate) dict.set(PDFName.of("Interpolate"), interpolate);
    doc.context.assign(info.ref, PDFRawStream.of(dict, out.bytes));
    replaced++;
  }
  hooks.onProgress?.(images.length, images.length);
  const bytes = await doc.save({ useObjectStreams: true });
  return { bytes, replaced, considered: images.length };
}

/**
 * The sharpest ladder step whose result fits `goalBytes`, found by bisecting the ladder (about five
 * passes instead of twelve). Returns the result of the best step that fit, or the coarsest step
 * tried if none did.
 */
export async function recompressToFit(
  original: Uint8Array,
  goalBytes: number,
  codec: ImageCodec,
  hooks: { onPass?: (pass: number, passes: number) => void; onProgress?: (done: number, total: number) => void; shouldStop?: () => boolean } = {},
): Promise<RecompressResult & { fits: boolean; setting: RecompressSetting }> {
  const n = SETTING_LADDER.length;
  const passes = 1 + Math.ceil(Math.log2(n));
  let pass = 0;
  const attempt = async (i: number) => {
    hooks.onPass?.(++pass, passes);
    return recompressImages(original, SETTING_LADDER[i], codec, hooks);
  };

  let best: (RecompressResult & { setting: RecompressSetting }) | null = null;
  const first = await attempt(0);
  if (first.bytes.length <= goalBytes) return { ...first, fits: true, setting: SETTING_LADDER[0] };

  let lo = 1;
  let hi = n - 1;
  let coarsest: (RecompressResult & { setting: RecompressSetting }) | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const r = await attempt(mid);
    if (mid === n - 1) coarsest = { ...r, setting: SETTING_LADDER[mid] };
    if (r.bytes.length <= goalBytes) {
      best = { ...r, setting: SETTING_LADDER[mid] };
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  if (best) return { ...best, fits: true };
  const last = coarsest ?? { ...(await attempt(n - 1)), setting: SETTING_LADDER[n - 1] };
  return { ...last, fits: false };
}


/** Every fourth step of the ladder, sharpest to coarsest, is measured for each picture. */
const SMART_STEP = 4;

/**
 * Fits the pictures into `goalBytes` the intelligent way: each picture is measured at several settings (its size
 * and how much worse it would look), and the budget is then spent where it costs the least quality -- a plain
 * diagram is shrunk hard, a detailed photo is kept sharp -- instead of one setting for everything.
 * Returns null when the codec cannot measure (the caller then uses the uniform search) or there are no pictures.
 * `minMaxSide` stops the plan from going coarser than that many pixels on a picture's long side.
 */
export async function recompressSmart(
  original: Uint8Array,
  goalBytes: number,
  codec: ImageCodec,
  hooks: { onProgress?: (done: number, total: number) => void; onPass?: (pass: number, passes: number) => void; shouldStop?: () => boolean } = {},
  opts: { minMaxSide?: number } = {},
): Promise<(RecompressResult & { fits: boolean }) | null> {
  if (!codec.profile) return null;
  const doc = await PDFDocument.load(original, { updateMetadata: false });
  const { images } = findImages(doc);
  if (images.length === 0) return null;

  const settings: RecompressSetting[] = [];
  for (let i = 0; i < SETTING_LADDER.length; i += SMART_STEP) settings.push(SETTING_LADDER[i]);
  const last = SETTING_LADDER[SETTING_LADDER.length - 1];
  if (settings[settings.length - 1] !== last) settings.push(last);
  const allowed = settings.filter((st) => st.maxSide >= (opts.minMaxSide ?? 0));
  if (allowed.length === 0) allowed.push(settings[0]);

  // Measure every picture once.
  const items: Candidate[][] = [];
  const settingOf: (RecompressSetting | null)[][] = []; // per item, per candidate: the setting, or null = keep
  hooks.onPass?.(1, 2);
  for (let i = 0; i < images.length; i++) {
    if (hooks.shouldStop?.()) throw new CompressStopped();
    hooks.onProgress?.(i, images.length);
    const info = images[i];
    const cands: Candidate[] = [{ bytes: info.storedBytes, distortion: 0 }];
    const sets: (RecompressSetting | null)[] = [null];
    const stream = doc.context.lookup(info.ref);
    const stored = stream instanceof PDFRawStream ? readImageBytes(stream, info) : null;
    if (stored) {
      let profiles: ({ bytes: number; distortion: number } | null)[] = [];
      try { profiles = await codec.profile(info, stored, allowed); } catch { profiles = []; }
      profiles.forEach((pr, k) => {
        if (pr && pr.bytes < info.storedBytes * MIN_GAIN) { cands.push({ bytes: pr.bytes, distortion: pr.distortion }); sets.push(allowed[k]); }
      });
    }
    items.push(cands);
    settingOf.push(sets);
  }
  hooks.onProgress?.(images.length, images.length);

  // Spend the budget, build, and tighten if the real file came out over.
  const storedTotal = images.reduce((n, im) => n + im.storedBytes, 0);
  const fixed = original.length - storedTotal;
  hooks.onPass?.(2, 2);
  let budget = goalBytes;
  let result: (RecompressResult & { fits: boolean }) | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const plan = allocate(items, budget, fixed);
    const r = await recompressImages(original, (_info, i) => settingOf[i][plan.choice[i]], codec, hooks);
    result = { ...r, fits: r.bytes.length <= goalBytes };
    if (result.fits || !plan.fits) break; // fits, or the plan already used the smallest choices
    budget -= Math.ceil((r.bytes.length - goalBytes) * 1.1) + 1024; // the estimate was a little low: aim lower and rebuild
  }
  return result;
}

/** The browser implementation: decode, scale on a canvas, write a JPEG. */
async function decodeBitmap(info: PdfImageInfo, stored: Uint8Array): Promise<ImageBitmap> {
    let bitmap: ImageBitmap;
    if (info.kind === "jpeg") {
      // "none": a PDF's JPEG ignores any EXIF orientation tag, so the browser must not apply it here.
      bitmap = await createImageBitmap(new Blob([stored as BlobPart], { type: "image/jpeg" }), { imageOrientation: "none" });
    } else {
      const rgba = new Uint8ClampedArray(info.width * info.height * 4);
      if (info.components === 3) {
        for (let i = 0, j = 0; i < stored.length; i += 3, j += 4) {
          rgba[j] = stored[i]; rgba[j + 1] = stored[i + 1]; rgba[j + 2] = stored[i + 2]; rgba[j + 3] = 255;
        }
      } else {
        for (let i = 0, j = 0; i < stored.length; i++, j += 4) {
          rgba[j] = rgba[j + 1] = rgba[j + 2] = stored[i]; rgba[j + 3] = 255;
        }
      }
      bitmap = await createImageBitmap(new ImageData(rgba, info.width, info.height));
    }
    return bitmap;
}

function grayOf(source: ImageBitmap, width: number, height: number): Uint8Array {
  const c = document.createElement("canvas");
  c.width = width; c.height = height;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported in this browser");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  const g = rgbaToGray(ctx.getImageData(0, 0, width, height).data);
  c.width = c.height = 0;
  return g;
}

export const browserImageCodec: ImageCodec = {
  async profile(info, stored, settings) {
    const bitmap = await decodeBitmap(info, stored);
    const cs = Math.min(1, PROFILE_COMPARE_SIDE / Math.max(bitmap.width, bitmap.height));
    const cw = Math.max(8, Math.round(bitmap.width * cs)), ch = Math.max(8, Math.round(bitmap.height * cs));
    const ref = grayOf(bitmap, cw, ch);
    const out: ({ bytes: number; distortion: number } | null)[] = [];
    for (const setting of settings) {
      const enc = await encodeScaled(bitmap, setting);
      if (!enc) { out.push(null); continue; }
      const cand = await createImageBitmap(new Blob([enc.bytes as BlobPart], { type: "image/jpeg" }));
      const g = grayOf(cand, cw, ch);
      cand.close();
      out.push({ bytes: enc.bytes.length, distortion: ssimDistortion(ref, g, cw, ch) });
    }
    bitmap.close();
    return out;
  },
  async reencode(info, stored, setting) {
    const bitmap = await decodeBitmap(info, stored);
    const enc = await encodeScaled(bitmap, setting);
    bitmap.close();
    return enc;
  },
};

async function encodeScaled(bitmap: ImageBitmap, setting: RecompressSetting): Promise<{ bytes: Uint8Array; width: number; height: number } | null> {
  const scale = Math.min(1, setting.maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", setting.quality));
  canvas.width = canvas.height = 0; // let the memory go now rather than at garbage collection
  if (!blob) return null;
  return { bytes: new Uint8Array(await blob.arrayBuffer()), width, height };
}
